import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import useImageOptimization from '../hooks/useImageOptimization';
import config from '../utils/config';
import '../styles/components/HomeCarousel.css';

const AUTO_ADVANCE_MS = 7000;

// Función para optimizar URLs de imágenes en móvil
const optimizeImageUrl = (url) => {
  if (!url) return url;
  const isMobile = window.innerWidth <= 768;
  
  // Si es móvil, reducir ancho de la imagen para ahorrar bandwidth
  if (isMobile && url.includes('?')) {
    return url.replace(/w=[0-9]+/, 'w=400').replace(/q=[0-9]+/, 'q=75');
  }
  return url;
};

const fallbacks = [
  {
    id: 'fallback-1',
    title: 'Formación que impulsa tu carrera',
    subtitle: 'Programas con enfoque práctico',
    description: 'Vive experiencias de aprendizaje que combinan teoría, práctica y acompañamiento profesional.',
    imageUrl: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80',
    buttonLabel: 'Explora nuestros talleres',
    buttonUrl: '/talleres',
  },
  {
    id: 'fallback-2',
    title: 'Aprende con especialistas',
    subtitle: 'Mentores que viven la profesión día a día',
    description: 'Nuestro equipo docente está integrado por profesionales en activo que comparten su experiencia contigo.',
    imageUrl: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1600&q=80',
    buttonLabel: 'Conoce al equipo',
    buttonUrl: '/nosotros',
  },
];

const HomeCarousel = () => {
  // Obtener la URL base del API centralizada
  const { API_BASE_URL } = config;

  // La portada debe seguir siendo útil aunque el API tarde o no esté disponible.
  const [slides, setSlides] = useState(fallbacks);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPaused, setIsPaused] = useState(false);

  const loadCarouselImages = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const fullUrl = `${API_BASE_URL}/home/carousel`;
      
      const response = await fetch(fullUrl, {
        cache: 'no-store',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      
      const data = Array.isArray(payload.data) ? payload.data : [];

      if (data.length === 0) {
        setSlides(fallbacks);
      } else {
        const processedSlides = data.map((item) => ({
          id: item.id,
          title: item.title,
          subtitle: item.subtitle,
          description: item.description,
          imageUrl: item.imageUrl,
          buttonLabel: item.buttonLabel,
          buttonUrl: item.buttonUrl,
        }));
        console.log(`🖼️ Imágenes procesadas:`, processedSlides);
        setSlides(processedSlides);
      }
    } catch (err) {
      console.error('❌ Error cargando carrusel:', err);
      console.error('❌ Stack:', err.stack);
      setError('No pudimos cargar las imágenes destacadas.');
      setSlides(fallbacks);
    } finally {
      setLoading(false);
      setCurrentIndex(0);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    loadCarouselImages();
  }, [loadCarouselImages]);

  // Preload next/prev images for smooth transitions
  useImageOptimization(slides, currentIndex);

  // Recargar carrusel cuando la página se vuelve visible (vuelve del AdminPanel)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadCarouselImages();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadCarouselImages]);

  useEffect(() => {
    if (slides.length <= 1 || isPaused) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, AUTO_ADVANCE_MS);

    return () => window.clearInterval(timer);
  }, [slides, isPaused]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };

  const activeSlide = slides[currentIndex];

  return (
    <section
      id="inicio"
      className="home-carousel"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      aria-label="Contenido destacado"
    >
      {loading && slides.length === 0 ? (
        <div className="carousel-loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          <div className="carousel-background">
            <AnimatePresence initial={false}>
              {activeSlide && (
                <motion.img
                  key={activeSlide.id}
                  src={optimizeImageUrl(activeSlide.imageUrl)}
                  alt={activeSlide.title}
                  className="carousel-image"
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  style={{ objectFit: 'cover', width: '100%', height: '100%', willChange: 'opacity' }}
                />
              )}
            </AnimatePresence>
            <div className="carousel-overlay" />
          </div>

          <div className="carousel-content container">
            <div className="carousel-meta">
              <AnimatePresence mode="wait">
                {activeSlide && (
                  <motion.div
                    key={`text-${activeSlide.id}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.45 }}
                  >
                    {activeSlide.subtitle && <span className="carousel-subtitle">{activeSlide.subtitle}</span>}
                    <h1 className="carousel-title">{activeSlide.title}</h1>
                    {activeSlide.description && <p className="carousel-description">{activeSlide.description}</p>}
                    {activeSlide.buttonLabel && activeSlide.buttonUrl && (
                      <a className="carousel-cta" href={activeSlide.buttonUrl}>
                        {activeSlide.buttonLabel}
                      </a>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="carousel-controls">
                <button
                  type="button"
                  onClick={handlePrev}
                  className="carousel-nav-btn"
                  aria-label="Anterior"
                >
                  <FaChevronLeft />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="carousel-nav-btn"
                  aria-label="Siguiente"
                >
                  <FaChevronRight />
                </button>
              </div>

              <div className="carousel-dots" role="tablist" aria-label="Selecciona diapositiva">
                {slides.map((slide, index) => {
                  const isActive = index === currentIndex;
                  return (
                    <button
                      key={slide.id ?? index}
                      type="button"
                      role="tab"
                      className={`carousel-dot ${isActive ? 'active' : ''}`}
                      aria-label={`Ir a la diapositiva ${index + 1}`}
                      aria-selected={isActive}
                      onClick={() => handleDotClick(index)}
                    />
                  );
                })}
              </div>

              {error && (
                <div className="carousel-error" role="status">
                  Mostrando contenido disponible mientras recuperamos las novedades.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default React.memo(HomeCarousel);

